import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import NetworkDomain from "_shared/network-domain/network-domain";
import EntityName from "legal-entities/entity-names/entity-name";
import { z } from "zod";

export type LegalEntityJSON = z.infer<typeof LegalEntitySchema.LegalEntityJSONZ>;
export type LegalEntityParams = z.infer<typeof LegalEntitySchema.LegalEntityZ>;

export default class LegalEntitySchema {
  public static readonly LegalEntityJSONZ = z.object({
    id: z.uuid(),
    name: z.string().nullish(),
    managerIds: z.array(z.uuid()).nullish(),
    isVendor: z.boolean().nullish(),
    isClient: z.boolean().nullish(),
    emailDomains: z.array(z.string()).nullish(),
  });

  public static readonly LegalEntityZ = z.object({
    id: z.instanceof(Guid),
    name: z.instanceof(EntityName).optional(),
    managerIds: z.array(z.instanceof(Guid)).optional(),
    isVendor: z.boolean().optional(),
    isClient: z.boolean().optional(),
    emailDomains: z.array(z.custom<NetworkDomain>()).optional(),
  });

  public static validate(values: unknown): LegalEntityParams {
    return parseOrThrow(LegalEntitySchema.LegalEntityZ, values, {
      logError: true,
      schemaName: "LegalEntitySchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): LegalEntityJSON {
    return parseOrThrow(LegalEntitySchema.LegalEntityJSONZ, json, {
      logError: true,
      schemaName: "LegalEntitySchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is LegalEntityParams {
    try {
      LegalEntitySchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is LegalEntityJSON {
    try {
      LegalEntitySchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: LegalEntityJSON): LegalEntityParams {
    const parsed = parseOrThrow(LegalEntitySchema.LegalEntityJSONZ, json, {
      logError: true,
      schemaName: "LegalEntitySchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return LegalEntitySchema.codec.decode(parsed);
  }

  public static deserializeJSONArray(jsonArray: unknown): LegalEntityParams[] {
    return (jsonArray as unknown[]).map((json) => LegalEntitySchema.deserialize(json as LegalEntityJSON));
  }

  public static serialize(params: LegalEntityParams): LegalEntityJSON {
    const parsed = LegalEntitySchema.validate(params);
    return LegalEntitySchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(LegalEntitySchema.LegalEntityJSONZ, LegalEntitySchema.LegalEntityZ, {
    decode: (data) => ({
      id: Guid.fromJSON(data.id)!,
      name: EntityName.fromJSON(data.name) ?? undefined,
      managerIds: Guid.fromJSONArray(data.managerIds) ?? [],
      isVendor: data.isVendor ?? undefined,
      isClient: data.isClient ?? undefined,
      emailDomains: data.emailDomains?.map((domain) => new NetworkDomain(domain)) ?? [],
    }),
    encode: (entity) => ({
      id: entity.id.toJSON(),
      name: entity.name?.toJSON(),
      managerIds: entity.managerIds?.map((guid) => guid.toJSON()) ?? [],
      isVendor: entity.isVendor,
      isClient: entity.isClient,
      emailDomains: entity.emailDomains?.map((domain) => domain.value) ?? [],
    }),
  });
}
